import useGetMyPermissionsFromCache from '~/features/user/hooks/useGetMyPermissionsFromCache'
import { EAccessLevel, EBaseAccess, ECapability } from '~/features/user/models'
import useGetAuthSessionQuery from '~/hooks/useGetAuthSessionQuery'
import { ECognitoGroups } from '~/models/auth'
import { store } from '~/store'

const isReadOrEditAccessLevel = (level?: EAccessLevel): boolean =>
  level === EAccessLevel.read || level === EAccessLevel.edit

const useUserAccess = () => {
  const { data: authSession } = useGetAuthSessionQuery()
  const userGroups = authSession?.groups || []
  const [orgPaymentTier, isImpersonated] = store((state) => [
    state.organization.paymentTier,
    !!state.user.impersonatorsUserId,
  ])

  const { data: permissions, isLoading: isLoadingPermissions } =
    useGetMyPermissionsFromCache()

  const isFreePlan = orgPaymentTier === ECognitoGroups.customerFreePlan
  const isBasicFreeTrial =
    orgPaymentTier === ECognitoGroups.customerBasicFreeTrialPlan
  const isPlusUser = isImpersonated
    ? orgPaymentTier === ECognitoGroups.customerPlusPlan
    : userGroups.includes(ECognitoGroups.customerPlusPlan)
  const isBasicUser = isImpersonated
    ? orgPaymentTier === ECognitoGroups.customerBasicPlan
    : userGroups.includes(ECognitoGroups.customerBasicPlan)

  const isPaidUser = isPlusUser || isBasicUser || isBasicFreeTrial

  const isCustomAccess = permissions?.baseAccess === EBaseAccess.custom

  const userMgmtAccessLevel = permissions?.capabilities?.userManagement
  const canManageUsers = isReadOrEditAccessLevel(userMgmtAccessLevel)

  const orgConfigAccessLevel =
    permissions?.capabilities?.[ECapability.orgConfig]
  const canAccessOrgConfig =
    !isCustomAccess || isReadOrEditAccessLevel(orgConfigAccessLevel)

  const crmAccessLevel = permissions?.capabilities?.[ECapability.crm]
  const canAccessCrm =
    !isCustomAccess || isReadOrEditAccessLevel(crmAccessLevel)

  const reportingAccessLevel =
    permissions?.capabilities?.[ECapability.reporting]
  const canAccessReporting =
    !isCustomAccess || isReadOrEditAccessLevel(reportingAccessLevel)

  const campaignsAccessLevel =
    permissions?.capabilities?.[ECapability.campaigns]
  const canAccessCampaigns =
    !isCustomAccess || isReadOrEditAccessLevel(campaignsAccessLevel)

  const aiAssistantConfigAccessLevel =
    permissions?.capabilities?.[ECapability.aiAssistantConfig]
  const canAccessAiAssistantConfig =
    !isCustomAccess || isReadOrEditAccessLevel(aiAssistantConfigAccessLevel)

  const knowledgeBaseAccessLevel =
    permissions?.capabilities?.[ECapability.knowledgeBase]
  const canAccessKnowledgeBase =
    !isCustomAccess || isReadOrEditAccessLevel(knowledgeBaseAccessLevel)

  const voipConfigAccessLevel =
    permissions?.capabilities?.[ECapability.voipConfig]
  const canAccessVoipConfig =
    !isCustomAccess || isReadOrEditAccessLevel(voipConfigAccessLevel)

  const voipVoicemailsAccessLevel =
    permissions?.capabilities?.[ECapability.voipVoicemails]
  const canAccessVoipVoicemails =
    !isCustomAccess || isReadOrEditAccessLevel(voipVoicemailsAccessLevel)

  return {
    isFreePlan,
    isBasicFreeTrial,
    isPlusUser,
    isBasicUser,
    isPaidUser,
    isCustomAccess,
    canManageUsers,
    canAccessOrgConfig,
    canAccessCrm,
    canAccessReporting,
    canAccessCampaigns,
    canAccessAiAssistantConfig,
    canAccessKnowledgeBase,
    canAccessVoipConfig,
    canAccessVoipVoicemails,
    isLoadingPermissions,
  }
}

export default useUserAccess
